import React, { useState, useEffect, useRef } from 'react';
import { Input, Form, InputNumber, Select, DatePicker as AntDatePicker, TreeSelect, Card, Alert } from 'antd';
import DayJS from 'dayjs';
import TextArea from 'antd/lib/input/TextArea';
import ObjectID from 'bson-objectid';
import useSWR from 'swr';

import { moneyFormatter, nodeTitleResolver, currencyResolver, 
  useHandleSWR, datePartsFromMoment, momentFromEntry } from '~/libs/helpers';
import { Currencies } from '~/libs/constants';
import TagInput from '~/components/common/TagInput';
import CreateProjectModal from '~/components/modals/CreateProjectModal';
import { InfoBadge } from '~/components/common/InfoBadge';
import { InputAddIcon, InputMinusIcon, HorizontalSeparator, 
  FormHorizontalFields, JournalEntryButtonContainer } from '~/components/common/EditorStyles';

const InvoiceEditor = (props) => {
  const { invoice, onInvoiceUpdate } = props;
  const [ modal, setModal ] = useState();
  const { data: swrAccount } = useSWR('/api/account');
  const { data: account } = useHandleSWR(swrAccount, [], true);

  const settings = account?.settings;
  const paymentMethods = settings?.paymentMethods || [];
  const projects = settings?.projects || [];
  const currency = currencyResolver(invoice.currency);
  const payments = invoice.payments ? invoice.payments : [];
  const isExpert = invoice.isExpert;
  const isReadOnly = invoice.isPaid && invoice.isExpert;

  // If not "Expert" we only have one entry (the only one payment)
  const singleEntry = !isExpert && invoice.payments && invoice.payments.length > 0 ? invoice.payments[0] : null;

  // Calculate totals (payments, and net)
  const totalPaymentsAmount = payments.reduce((a, b) => a + b.amount, 0);
  const totalAmount = invoice.amount + invoice.jctAmount + invoice.withheldAmount;

  // Auto-refresh the amount of JCT if the JCT % changes, or if the brut amount changes.
  const isFirstRunOfJctAmountRefresh = useRef(true);
  useEffect(() => {
    if (isFirstRunOfJctAmountRefresh.current) {
      isFirstRunOfJctAmountRefresh.current = false;
      return;
    }
    const jctAmount = Math.round(invoice.amount * (invoice.jctTax / 100));
    onInvoiceUpdate({ ...invoice, jctAmount });
  }, [invoice.amount, invoice.jctTax]);

  // Auto-refresh the amount of Withheld if the Withheld % changes.
  const isFirstRunOfWithheldAmountRefresh = useRef(true);
  useEffect(() => {
    if (isFirstRunOfWithheldAmountRefresh.current) {
      isFirstRunOfWithheldAmountRefresh.current = false;
      return;
    }
    const withheldAmount = -1 * Math.round(invoice.amount * (invoice.withheldTax / 100));
    onInvoiceUpdate({ ...invoice, withheldAmount });
  }, [invoice.amount, invoice.withheldTax]);

  // Auto-refresh the amount of Withheld and JCT if the brut amount changes.
  const isFirstRunOfAmountRefresh = useRef(true);
  useEffect(() => {
    if (isFirstRunOfAmountRefresh.current) {
      isFirstRunOfAmountRefresh.current = false;
      return;
    }
    const jctAmount = Math.round(invoice.amount * (invoice.jctTax / 100));
    const withheldAmount = -1 * Math.round(invoice.amount * (invoice.withheldTax / 100));
    onInvoiceUpdate({ ...invoice, jctAmount, withheldAmount });
  }, [invoice.amount]);

  // If there are no payments yets, let's create defaults payments

  useEffect(() => {
    if (paymentMethods?.length < 1) {
      return;
    }
    if (!totalAmount) {
      return;
    }
    // Simple & No Payment => Create a payment
    const now = new Date();
    if (!isExpert && !singleEntry) {
      let freshPayments = [{
        _id: ObjectID(),
        date: invoice.date,
        year: invoice.year,
        month: invoice.month,
        day: invoice.day,
        debitSubAccountId: paymentMethods[0]._id,
        amount: invoice.amount,
      }];
      onInvoiceUpdate({ ...invoice, payments: freshPayments });
    }
    // No Payment & isPaid => Create a payment
    else if (payments.length === 0 && invoice.isPaid) {
      let freshPayments = [...payments];
      freshPayments.push({ _id: ObjectID(), _new: true, date: new Date(), 
        year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate(),
        debitSubAccountId: paymentMethods[0]._id, amount: totalAmount });
        freshPayments = freshPayments.sort((a, b) => a.date - b.date);
      onInvoiceUpdate({ ...invoice, payments: freshPayments });
    }
    // If only one payment and if it's paid OR the payment is above the total amount
    else if (payments.length === 1 && (invoice.isPaid || singleEntry)) {
      payments[0].amount = totalAmount;
      payments[0].date = invoice.date;
      payments[0].year = invoice.year;
      payments[0].month = invoice.month;
      payments[0].day = invoice.day;
      onInvoiceUpdate({ ...invoice, isPaid: true, payments: payments });
    }
  }, [payments, invoice.amount, invoice.date, paymentMethods]);

  const onAddProject = () => {
    setModal('createProject');
  }

  /* PAYMENT METHODS */

  const mapPaymentMethodToTree = (items) => {
    return items.map(x => ({
      key: x._id,
      value: x._id,
      disabled: x.name === 'personal-advance' && invoice.personal,
      title: nodeTitleResolver(paymentMethods, x._id),
      items: (x.items && x.items.length > 0) ? mapPaymentMethodToTree(x.items) : null
    }));
  }
  let paymentMethodData = mapPaymentMethodToTree(paymentMethods);

  /* PROJECTS */

  const mapProjectToTree = (items) => {
    return items.map(x => ({
      key: x._id,
      value: x._id,
      title: nodeTitleResolver(projects, x._id),
      items: (x.items && x.items.length > 0) ? mapPaymentMethodToTree(x.items) : null
    }));
  }
  let projectData = mapProjectToTree(projects);

  /* ENTRIES RELATED FUNCTIONS */

  const onEntryUpdate = (entry) => {
    //console.log('Entry Update', { entry });
    let freshPayments = [...invoice.payments.filter(x => x._id !== entry._id), entry];
    freshPayments = freshPayments.sort((a, b) => a.date - b.date);
    const freshInvoice = { ...invoice, payments: freshPayments };
    onInvoiceUpdate(freshInvoice);
  }

  const onAddRelatedEntry = () => {
    const now = new Date();
    let freshPayments = [...(invoice.payments ? invoice.payments : []), {
      _id: ObjectID(),
      date: new Date(),
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      day: now.getDate(),
      debitSubAccountId: paymentMethods[0]._id,
      amount: totalAmount - totalPaymentsAmount,
    }];
    freshPayments = freshPayments.sort((a, b) => a.date - b.date);
    onInvoiceUpdate({ ...invoice, payments: freshPayments });
  }

  const onRemoveRelatedEntry = (entry) => {
    const freshPayments = [...invoice.payments.filter(x => x._id !== entry._id)];
    const freshInvoice = {...invoice, payments: freshPayments };
    onInvoiceUpdate(freshInvoice);
  }

  return (
    <Form layout="vertical">

      <FormHorizontalFields>

        <Form.Item label={<span>Project <InfoBadge tooltip='The project this invoice is related to.' /></span>}>
          <TreeSelect treeData={projectData} value={invoice.projectId} disabled={isReadOnly}
            showSearch={true} treeDefaultExpandAll={true} suffixIcon={<InputAddIcon onClick={onAddProject} />}
            filterTreeNode={(input, treeNode) => {
              input = input.toLowerCase();
              return treeNode.title.toLowerCase().includes(input);
            }}
            onChange={(value) => { onInvoiceUpdate({...invoice, projectId: value }); }}>
          </TreeSelect>
        </Form.Item>

        <Form.Item label={<span>Subtotal Amount <InfoBadge tooltip='???' /> </span>} style={{ marginLeft: 20 }}>
          <Input.Group compact style={{ display: 'flex' }}>
            <InputNumber value={invoice.amount ? parseFloat(invoice.amount) : null} disabled={isReadOnly}
              placeholder="Amount" step={currency.steps} precision={currency.decimals}
              formatter={moneyFormatter}
              parser={value => value.replace(/\$\s?|(,*)/g, '')}
              onChange={(value) => onInvoiceUpdate({...invoice, amount: value })} />
            <Select value={invoice.currency} disabled={isReadOnly} 
              onChange={(value) => onInvoiceUpdate({...invoice, currency: value })}>
              {Currencies.map(x => <Select.Option key={'invoice-currency-' + x.code} value={x.code}>{x.code}</Select.Option>)}
            </Select>
          </Input.Group>
        </Form.Item>

      </FormHorizontalFields>

      <div style={{ marginTop: -20, marginBottom: 20 }}>

        {invoice.jctTax > 0 && <FormHorizontalFields style={{ marginBottom: 5 }}>
          <div></div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>JCT Tax (10%)</div>
          <div style={{ marginLeft: 20, marginBottom: 0 }}>
            <Input.Group compact style={{ display: 'flex' }}>
              <InputNumber value={invoice.jctAmount ? parseFloat(invoice.jctAmount) : null} 
                disabled={isReadOnly} 
                placeholder="Amount" step={currency.steps} precision={currency.decimals}
                formatter={moneyFormatter}
                parser={value => value.replace(/\$\s?|(,*)/g, '')}
                onChange={(value) => onInvoiceUpdate({...invoice, jctAmount: value })} />            
              <Select value={invoice.jctTax} disabled={isReadOnly} 
                onChange={(value) => onInvoiceUpdate({...invoice, jctTax: value })}>
                <Select.Option value={0}>None</Select.Option>
                <Select.Option value={8}>8%</Select.Option>
                <Select.Option value={10}>10%</Select.Option>
              </Select>
            </Input.Group>
          </div>
        </FormHorizontalFields>}

        {invoice.withheldTax > 0 && <FormHorizontalFields>
          <div></div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>Withholding Tax</div>
          <div style={{ marginLeft: 20 }}>
            <Input.Group compact style={{ display: 'flex' }}>
              <InputNumber value={invoice.withheldAmount ? parseFloat(invoice.withheldAmount) : null} 
                disabled={isReadOnly}
                placeholder="Amount" step={currency.steps} precision={currency.decimals}
                formatter={moneyFormatter}
                parser={value => value.replace(/\$\s?|(,*)/g, '')}
                onChange={(value) => onInvoiceUpdate({...invoice, withheldAmount: value })} />          
              <Select value={invoice.withheldTax} disabled={isReadOnly} 
                onChange={(value) => onInvoiceUpdate({...invoice, withheldTax: value })}>
                <Select.Option value={0}>None</Select.Option>
                <Select.Option value={10.21}>10.21%</Select.Option>
                <Select.Option value={20.42}>20.42%</Select.Option>
              </Select>
            </Input.Group>
          </div>
        </FormHorizontalFields>}

      </div>

      <FormHorizontalFields>

        <Form.Item label={<span>Invoice Date <InfoBadge tooltip={<span>???</span>} /> </span>}>
          <AntDatePicker value={momentFromEntry(invoice)} disabled={isReadOnly}
            onChange={(value) => { 
              const { year, month, day } = datePartsFromMoment(value);
              onInvoiceUpdate({...invoice, date: value.toDate(), year, month, day }); 
            }} 
          />
        </Form.Item>

        <Form.Item label={<span>Details <InfoBadge tooltip='This is usually the name of your provider. You can add additional information such as the month of the invoice, a reference, etc.' /> </span>}>
          <Input value={invoice.details} onChange={(ev) => onInvoiceUpdate({...invoice, details: ev.target.value })} />
        </Form.Item>

        <Form.Item label={<span>Total Amount <InfoBadge tooltip='???' /> </span>}>
          <InputNumber value={totalAmount} disabled={true} precision={currency.decimals}
            formatter={moneyFormatter} parser={value => value.replace(/\$\s?|(,*)/g, '')}
          />
        </Form.Item>

        {singleEntry && <Form.Item label={<span>Method <InfoBadge tooltip='How the payment was made.' /> </span>}>
          <TreeSelect treeData={paymentMethodData} value={singleEntry.debitSubAccountId} disabled={isReadOnly}
            showSearch={true} treeDefaultExpandAll={true}
            filterTreeNode={(input, treeNode) => {
              input = input.toLowerCase();
              return treeNode.title.toLowerCase().includes(input);
            }}
            onChange={(value) => { onEntryUpdate({...singleEntry, debitSubAccountId: value }); }}>
          </TreeSelect>
        </Form.Item>}

      </FormHorizontalFields>

      {invoice?.isExpert && <Card style={{ background: '#f9f9f9' }} size="small" title={<span style={{ fontSize: 18 }}>
        <span>
          Payments
        </span>
        <span style={{ marginLeft: 20 }}>
          {!invoice.isPaid && <InputAddIcon onClick={() => onAddRelatedEntry()} />}
        </span>
       </span>}>

        {payments.map((entry, i) => 
          <FormHorizontalFields key={entry._id} style={{ marginBottom: -20 }}>

            <Form.Item label={i === 0 && <span>Date <InfoBadge tooltip={<span>???</span>} /> </span>}>
              <AntDatePicker value={momentFromEntry(entry)} disabled={isReadOnly} allowClear={false}
                onChange={(value) => {
                  const { year, month, day } = datePartsFromMoment(value);
                  onEntryUpdate({...entry, year, month, day }); 
                }} 
              />
            </Form.Item>

            <Form.Item label={i === 0 && <span>Method <InfoBadge tooltip='How the payment was made.' /> </span>}>
              <TreeSelect treeData={paymentMethodData} value={entry.debitSubAccountId} disabled={isReadOnly}
                showSearch={true} treeDefaultExpandAll={true}
                filterTreeNode={(input, treeNode) => {
                  input = input.toLowerCase();
                  return treeNode.title.toLowerCase().includes(input);
                }}
                onChange={(value) => { onEntryUpdate({...entry, debitSubAccountId: value }); }}>
              </TreeSelect>
            </Form.Item>

            <Form.Item label={i === 0 && <span>Amount <InfoBadge tooltip='???' /> </span>}>
              <Input.Group compact style={{ display: 'flex' }}>
                <InputNumber value={parseFloat(entry.amount ? entry.amount : 0)} disabled={isReadOnly}
                  placeholder="Amount" step={currency.step} precision={currency.decimals}
                  formatter={moneyFormatter}
                  parser={value => value.replace(/\$\s?|(,*)/g, '')}
                  onChange={(value) => onEntryUpdate({...entry, amount: value })} />
              </Input.Group>
            </Form.Item>
            
            <JournalEntryButtonContainer>
              {!invoice.isPaid && <InputMinusIcon onClick={() => onRemoveRelatedEntry(entry)} />}
            </JournalEntryButtonContainer>

          </FormHorizontalFields> 
        )}

        {!invoice.isPaid && totalPaymentsAmount === totalAmount && <Alert message="It seems like this invoice was completely paid. If so, please change the status to 'Paid'." type="success" />}

        {totalPaymentsAmount > totalAmount && <Alert message={<span>The total of the payments ({currency.symbol}{moneyFormatter(totalPaymentsAmount)}) is superior to the total amount ({currency.symbol}{moneyFormatter(totalAmount)}).</span>} type="error" />}

        {totalPaymentsAmount < totalAmount && <Alert message={<span>Your customer (project) still owes you {currency.symbol}{moneyFormatter(totalAmount - totalPaymentsAmount)}.</span>} type="error" />}

      </Card>}

      <HorizontalSeparator />

      <FormHorizontalFields>

        <Form.Item label={<span>Notes <InfoBadge tooltip='Notes are for your eyes only, and exclusively available in Tanukin.' /> </span>}>
          <TextArea value={invoice.notes} onChange={(ev) => onInvoiceUpdate({...invoice, notes: ev.target.value })} />
        </Form.Item>

        <Form.Item label={<span>Due On <InfoBadge tooltip={<span>???</span>} /> </span>}>
          <AntDatePicker value={invoice.dueOn ? DayJS(invoice.dueOn) : null} disabled={isReadOnly}
            onChange={(value) => { onInvoiceUpdate({...invoice, dueOn: value ? value.toDate() : null }); }} />
        </Form.Item>

        <Form.Item label={<span>Tags <InfoBadge tooltip={<span>Tags are optional, but allows invoices to be searched and grouped really easily.</span>} /> </span>}>
          <TagInput value={invoice.tags} onChange={(tags) => onInvoiceUpdate({...invoice, tags })} />
        </Form.Item>

      </FormHorizontalFields>

      <CreateProjectModal visible={modal === 'createProject'} 
        onChange={(value) => onInvoiceUpdate({...invoice, projectId: value })}
        onClose={() => setModal()} />
      
    </Form>
  );
}

export default InvoiceEditor;